import * as Yup from 'yup';

// FNP-1248 — validation for the CONNECT form.
//
// ⚠️ VALIDATE SHAPE, NOT POLICY. The provider is the authority on whether a
// credential works, and it says so with its own message. What is checked here
// is only what would be a WASTED ROUND TRIP: a missing provider, an empty
// display name, an ARN that is not an ARN. Whether the role exists, whether its
// trust policy is right, whether the token has the needed scopes — none of that
// is knowable here and none of it is guessed at.
//
// ⚠️ THE CREDENTIAL FIELDS ARE CONDITIONALLY REQUIRED ON `credentialType`.
//
// Exactly one of roleArn/apiToken is supplied, chosen by the provider. A schema
// requiring both would make every submission invalid; one requiring neither
// would let an empty credential reach the API. `when()` is what ties the
// requirement to the discriminator the form already tracks.

// Deliberately loose: an IAM role ARN's shape, not a claim the role exists.
// arn:<partition>:iam::<account>:role/<path><name>
const ROLE_ARN = /^arn:[a-z0-9-]+:iam::\d{12}:role\/.+$/;

export const RecordSchema = Yup.object()
                               .shape({
                                          providerId: Yup.string()
                                                         .required('Choose a DNS provider'),

                                          displayName: Yup.string()
                                                          .required('Required')
                                                          .max(128, 'Keep the name under 128 characters')
                                                          .test(
                                                              'not-only-whitespace',
                                                              'Give this connection a name',
                                                              value => !value || value.trim()
                                                                                      .length > 0,
                                                          ),

                                          // Tracked in the form so the credential fields can branch on
                                          // it. Not sent to the API — the provider owns this fact.
                                          credentialType: Yup.string()
                                                             .oneOf(['ASSUMED_ROLE', 'API_TOKEN'])
                                                             .required(),

                                          roleArn: Yup.string()
                                                      .when('credentialType', {
                                                          is: 'ASSUMED_ROLE',
                                                          then: schema => schema
                                                              .required('Enter the ARN of the role in your AWS account')
                                                              .matches(
                                                                  ROLE_ARN,
                                                                  // Same example as the field's placeholder, deliberately —
                                                                  // an error message showing a different name than the one
                                                                  // the operator was just looking at reads as a second,
                                                                  // conflicting requirement.
                                                                  'Enter a role ARN, for example arn:aws:iam::123456789012:role/FlexiNetwork-Domains-IntegrationRole',
                                                              ),
                                                          // Not merely optional — STRIPPED, so a value typed
                                                          // before switching provider cannot ride along into a
                                                          // token connection.
                                                          otherwise: schema => schema.strip(),
                                                      }),

                                          apiToken: Yup.string()
                                                       .when('credentialType', {
                                                           is: 'API_TOKEN',
                                                           then: schema => schema
                                                               .required('Enter the API token issued by your provider'),
                                                           // ⚠️ STRIPPED for the same reason, and it matters more
                                                           // here: a token typed against one provider must not
                                                           // survive a switch to another and be transmitted to
                                                           // the wrong vendor.
                                                           otherwise: schema => schema.strip(),
                                                       }),

                                          // ⚠️ FNP-1233 — a plain boolean, always present, defaulting
                                          // to false in initialValues. Not `.required()`: false is a
                                          // legitimate value and Yup's required() rejects it.
                                          writable: Yup.boolean(),
                                      });

// FNP-1304 — validation for the RENAME form.
//
// ⚠️ THE SAME THREE RULES AS `RecordSchema.displayName`, DELIBERATELY DUPLICATED
// RATHER THAN EXTRACTED. The connect form's field and this one answer the same
// question today, but they are not the same field: connect names a connection
// that does not exist yet, rename replaces the label on one that does. Sharing a
// single Yup fragment would make a future constraint that belongs to only one of
// them — a uniqueness check on connect, say — silently apply to both.
//
// ⚠️ NOT A UNIQUENESS CHECK, AND NOT THE PLACE FOR ONE. `connectProvider` does
// not enforce unique names, so two connections can already share one; a check
// here would refuse a rename to a name the connect form would have accepted.
// See the schema comment on `RenameProviderConnectionInput` in the API.
export const RenameSchema = Yup.object()
                               .shape({
                                          displayName: Yup.string()
                                                          .required('Required')
                                                          .max(128, 'Keep the name under 128 characters')
                                                          .test(
                                                              'not-only-whitespace',
                                                              'Give this connection a name',
                                                              value => !value || value.trim()
                                                                                      .length > 0,
                                                          ),
                                      });
